/**
 * UPS (uninterruptible power supply) catalog — 13 verified SKUs across the
 * five brands most commonly specced into European CCTV rack designs:
 *   • APC by Schneider Electric — Back-UPS Pro (SOHO), Smart-UPS (line-
 *     interactive rack), Smart-UPS On-Line / Ultra (online + Li-Ion)
 *   • Eaton — 5P (tower line-interactive), 5PX G2 (rack/tower line-
 *     interactive), 9PX (online double-conversion)
 *   • CyberPower — OR (1U line-interactive), OL (online), PR (sinewave
 *     line-interactive)
 *   • Ever — Polish brand, Sinline (line-interactive tower) and Powerline
 *     RT PLUS (online rack/tower)
 *   • Salicru — Spanish brand, SLC TWIN PRO2 (online tower)
 *
 * Every spec on this page was lifted directly from the manufacturer's
 * datasheet or product page; where the canonical apc.com / salicru.com
 * URLs returned 403 / TLS errors to WebFetch, a distributor that quotes
 * the datasheet verbatim (Mouser, APCGuard, euautomation, NSSE, netceed,
 * ekocell) was used and noted in the `// SRC:` line above each entry.
 *
 * Fields that the datasheet does NOT publish unambiguously (e.g. the
 * "18/7 min" runtime on the Salicru SLC where neither value is labelled
 * half- vs full-load) are OMITTED rather than guessed.
 *
 * Compiled: 2026-05-16, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const UPS_CATALOG: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // APC by Schneider Electric
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.mouser.com/datasheet/2/357/1/BR1500MS2_document-3053125.pdf
  //      "Back-UPS Pro BR1500MS2 — 1500VA / 900W, Tower, 120V, 10× NEMA
  //      5-15R outlets (6 battery+surge, 4 surge-only) + 2× USB charging
  //      (1× Type-A, 1× Type-C)". Line-interactive AVR. Runtime 12 min
  //      at half load. SLA 9 Ah. NOTE: this is a 120V model — quoted
  //      here because it's the volume seller; EU installs typically use
  //      BR1600SI (Schuko) or BR1500MI (IEC) variants of the same family.
  {
    id: 'apc-br1500ms2',
    manufacturer: 'APC by Schneider Electric',
    model: 'BR1500MS2',
    category: 'ups',
    tagline:
      'Back-UPS Pro BR1500MS2 — 1500 VA / 900 W tower, line-interactive, 10× NEMA + USB charging',
    verified: true,
    datasheetUrl: 'https://www.mouser.com/datasheet/2/357/1/BR1500MS2_document-3053125.pdf',
    specs: {
      capacityVA: 1500,
      capacityW: 900,
      topology: 'line-interactive',
      batteryType: 'SLA',
      formFactor: 'tower',
      outletsCount: 10,
      outletType: '10× NEMA 5-15R (6 battery+surge, 4 surge-only) + 2× USB charging',
      runtimeAtHalfLoadMin: 12,
      networkManagement: 'usb-only',
      weightKg: 12.5,
      depthMm: 368,
    },
  },

  // SRC: https://www.apcguard.com.au/datasheets/smart-ups_smt1500rmi2uc.pdf
  //      "Smart-UPS SMT1500RMI2UC — Line Interactive, 1500VA / 1000W,
  //      Rackmount 2U, 230V, 4× IEC C13 outlets, SmartConnect Port +
  //      SmartSlot, AVR, LCD". The classic small-rack workhorse — every
  //      4-8-camera SMB NVR has one of these. Runtime omitted: distributors
  //      report conflicting figures (7/16 vs 3/11.5 min) so we don't ship
  //      either; APC's Runtime Tool is the source of truth.
  {
    id: 'apc-smt1500rmi2uc',
    manufacturer: 'APC by Schneider Electric',
    model: 'SMT1500RMI2UC',
    category: 'ups',
    tagline:
      'Smart-UPS SMT1500RMI2UC — 1500 VA / 1000 W, 2U rack, line-interactive, 4× C13, SmartConnect',
    verified: true,
    datasheetUrl: 'https://www.apcguard.com.au/datasheets/smart-ups_smt1500rmi2uc.pdf',
    specs: {
      capacityVA: 1500,
      capacityW: 1000,
      topology: 'line-interactive',
      batteryType: 'SLA',
      formFactor: 'rack-2U',
      outletsCount: 4,
      outletType: '4× IEC 320 C13',
      networkManagement: 'snmp-slot',
      weightKg: 26.1,
      depthMm: 477,
    },
  },

  // SRC: https://assets.euautomation.com/uploads/parts/datasheet/10/srt3000rmxli.pdf
  //      "Smart-UPS On-Line SRT3000RMXLI — 3kVA / 2.7kW, Rackmount 2U,
  //      230V, 8× IEC C13 + 2× IEC C19, SmartSlot, Extended runtime,
  //      W/ rail kit". Online double-conversion (zero transfer time, pure
  //      sine-wave) — the standard pick for 16+ camera installs and
  //      lithium-battery sites that need clean power.
  {
    id: 'apc-srt3000rmxli',
    manufacturer: 'APC by Schneider Electric',
    model: 'SRT3000RMXLI',
    category: 'ups',
    tagline:
      'Smart-UPS On-Line SRT3000RMXLI — 3000 VA / 2700 W, 2U rack, online, 8× C13 + 2× C19',
    verified: true,
    datasheetUrl: 'https://assets.euautomation.com/uploads/parts/datasheet/10/srt3000rmxli.pdf',
    specs: {
      capacityVA: 3000,
      capacityW: 2700,
      topology: 'online',
      batteryType: 'SLA',
      formFactor: 'rack-2U',
      outletsCount: 10,
      outletType: '8× IEC 320 C13 + 2× IEC 320 C19',
      runtimeAtHalfLoadMin: 11.8,
      runtimeAtFullLoadMin: 4,
      networkManagement: 'snmp-slot',
      weightKg: 31.3,
      depthMm: 635,
    },
  },

  // SRC: https://www.apcguard.com.au/datasheets/Smart-UPS%20Ultra_SRTL3KRM1UIC.pdf
  //      "Smart-UPS Ultra On-Line SRTL3KRM1UIC — 3000VA, Lithium-ion,
  //      Rack/Tower 1U, 230V, 3× C13 + 2× C19, SmartConnect, Extended
  //      runtime". Li-Ion roughly doubles battery cycle life vs SLA and
  //      halves rack-U footprint — modern flagship for dense CCTV racks.
  //      NOTE: requested SRTL3KRM1UI substituted with -C (SmartConnect)
  //      suffix — bare ...UI isn't a finished APC SKU. The -NC variant
  //      adds embedded NMC instead of SmartConnect (snmp-built-in).
  {
    id: 'apc-srtl3krm1uic',
    manufacturer: 'APC by Schneider Electric',
    model: 'SRTL3KRM1UIC',
    category: 'ups',
    tagline:
      'Smart-UPS Ultra SRTL3KRM1UIC — 3000 VA / 3000 W, 1U rack/tower, online Li-Ion',
    verified: true,
    datasheetUrl: 'https://www.apcguard.com.au/datasheets/Smart-UPS%20Ultra_SRTL3KRM1UIC.pdf',
    specs: {
      capacityVA: 3000,
      capacityW: 3000,
      topology: 'online',
      batteryType: 'Li-Ion',
      formFactor: 'tower-or-rack',
      outletsCount: 5,
      outletType: '3× IEC 320 C13 + 2× IEC 320 C19',
      runtimeAtHalfLoadMin: 10,
      runtimeAtFullLoadMin: 4,
      networkManagement: 'usb+serial',
      weightKg: 15.9,
      depthMm: 560,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Eaton
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.eaton.com/gb/en-gb/skuPage.5P1550I.specifications.html
  //      "Eaton 5P 1550i — 1550VA / 1100W, Tower, 8× C13, Input C14,
  //      15.6 kg, 230 × 150 × 445 mm". Communication: USB + RS232 (not
  //      simultaneous) + relay contacts + optional Network-MS card slot
  //      for SNMP. Line-interactive AVR.
  {
    id: 'eaton-5p1550i',
    manufacturer: 'Eaton',
    model: '5P 1550i',
    category: 'ups',
    tagline:
      '5P 1550i — 1550 VA / 1100 W tower, line-interactive, 8× C13, optional Network-MS',
    verified: true,
    datasheetUrl: 'https://www.eaton.com/gb/en-gb/skuPage.5P1550I.specifications.html',
    specs: {
      capacityVA: 1550,
      capacityW: 1100,
      topology: 'line-interactive',
      batteryType: 'SLA',
      formFactor: 'tower',
      outletsCount: 8,
      outletType: '8× IEC 320 C13',
      runtimeAtFullLoadMin: 4,
      networkManagement: 'snmp-slot',
      weightKg: 15.6,
      depthMm: 445,
    },
  },

  // SRC: https://www.eaton.com/content/dam/eaton/products/backup-power-ups-surge-it-power-distribution/backup-power-ups/eaton-5px-g2-ups/eaton-5px-g2-datasheet-en-gb-anz.pdf
  //      "Eaton 5PX 1500i RT2U G2 — 1500VA / 1500W (PF=1.0), Input C14,
  //      8× C13, Rack/Tower 2U, 22.4 kg, 448 mm depth. Runtime 5 min at
  //      full load, 14 min at 50% load". Pure sine-wave, line-interactive.
  //      G2 generation adds 100% real-power rating (1500 VA = 1500 W).
  {
    id: 'eaton-5px1500irt2ug2',
    manufacturer: 'Eaton',
    model: '5PX 1500i RT2U G2',
    category: 'ups',
    tagline:
      '5PX 1500i RT2U G2 — 1500 VA / 1500 W rack/tower 2U, line-interactive PF=1.0, 8× C13',
    verified: true,
    datasheetUrl:
      'https://www.eaton.com/content/dam/eaton/products/backup-power-ups-surge-it-power-distribution/backup-power-ups/eaton-5px-g2-ups/eaton-5px-g2-datasheet-en-gb-anz.pdf',
    specs: {
      capacityVA: 1500,
      capacityW: 1500,
      topology: 'line-interactive',
      batteryType: 'SLA',
      formFactor: 'tower-or-rack',
      outletsCount: 8,
      outletType: '8× IEC 320 C13',
      runtimeAtHalfLoadMin: 14,
      runtimeAtFullLoadMin: 5,
      networkManagement: 'snmp-slot',
      weightKg: 22.4,
      depthMm: 448,
    },
  },

  // SRC: https://www.nsse.co.uk/assets/pdf/Eaton-9PX-3000W-RT2U-Datasheet-9PX3000IRT2U.pdf
  //      "Eaton 9PX 3000i RT2U — 3000VA / 3000W, Input C20, 8× C13 +
  //      2× C19, Rack/tower 2U, Sealed lead-acid hot-swappable, USB +
  //      RS232 + optional comm card slot, 27.57 kg, 60.5 cm depth,
  //      6 minutes at full load". Online double-conversion. Li-Ion variant
  //      exists as separate SKU 9PX3000IRT2U-L (22.8 kg).
  {
    id: 'eaton-9px3000irt2u',
    manufacturer: 'Eaton',
    model: '9PX 3000i RT2U',
    category: 'ups',
    tagline:
      '9PX 3000i RT2U — 3000 VA / 3000 W rack/tower 2U, online, hot-swap SLA, 8× C13 + 2× C19',
    verified: true,
    datasheetUrl:
      'https://www.nsse.co.uk/assets/pdf/Eaton-9PX-3000W-RT2U-Datasheet-9PX3000IRT2U.pdf',
    specs: {
      capacityVA: 3000,
      capacityW: 3000,
      topology: 'online',
      batteryType: 'SLA',
      formFactor: 'tower-or-rack',
      outletsCount: 10,
      outletType: '8× IEC 320 C13 + 2× IEC 320 C19',
      runtimeAtFullLoadMin: 6,
      networkManagement: 'snmp-slot',
      weightKg: 27.57,
      depthMm: 605,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // CyberPower
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.cyberpower.com/eu/en/product/sku/or1500erm1u
  //      "OR1500ERM1U — 1500 VA / 900 W, 6× IEC C13 (4 battery+surge,
  //      2 surge-only), Half Load Runtime 11 min, Full Load 2.5 min,
  //      14.9 kg, 389 mm depth". Line-interactive AVR, 1U rack — the
  //      ultra-thin pick where every U counts. SNMP via optional
  //      RMCARD205 add-on.
  {
    id: 'cyberpower-or1500erm1u',
    manufacturer: 'CyberPower',
    model: 'OR1500ERM1U',
    category: 'ups',
    tagline:
      'OR1500ERM1U — 1500 VA / 900 W, 1U rack, line-interactive, 6× C13, RMCARD-ready',
    verified: true,
    datasheetUrl: 'https://www.cyberpower.com/eu/en/product/sku/or1500erm1u',
    specs: {
      capacityVA: 1500,
      capacityW: 900,
      topology: 'line-interactive',
      batteryType: 'SLA',
      formFactor: 'rack-1U',
      outletsCount: 6,
      outletType: '6× IEC 320 C13 (4 battery+surge, 2 surge-only)',
      runtimeAtHalfLoadMin: 11,
      runtimeAtFullLoadMin: 2.5,
      networkManagement: 'snmp-slot',
      weightKg: 14.9,
      depthMm: 389,
    },
  },

  // SRC: https://www.cyberpower.com/au/en/product/sku/ol2000ertxl2u
  //      "OL2000ERTXL2U — 2000 VA / 1800 W, Online Double Conversion,
  //      8× IEC C13 + 1× IEC C19, 2U rack/tower, half-load 20 min,
  //      full-load 6.5 min, USB + Serial, 32 kg, 600 mm depth". SNMP via
  //      optional RMCARD. Long runtime variant of the OL family — the
  //      "TX" suffix indicates extended-runtime battery option.
  {
    id: 'cyberpower-ol2000ertxl2u',
    manufacturer: 'CyberPower',
    model: 'OL2000ERTXL2U',
    category: 'ups',
    tagline:
      'OL2000ERTXL2U — 2000 VA / 1800 W, 2U rack/tower, online, 8× C13 + 1× C19, extended runtime',
    verified: true,
    datasheetUrl: 'https://www.cyberpower.com/au/en/product/sku/ol2000ertxl2u',
    specs: {
      capacityVA: 2000,
      capacityW: 1800,
      topology: 'online',
      batteryType: 'SLA',
      formFactor: 'tower-or-rack',
      outletsCount: 9,
      outletType: '8× IEC 320 C13 + 1× IEC 320 C19',
      runtimeAtHalfLoadMin: 20,
      runtimeAtFullLoadMin: 6.5,
      networkManagement: 'usb+serial',
      weightKg: 32,
      depthMm: 600,
    },
  },

  // SRC: https://www.cyberpower.com/eu/en/product/sku/pr2200ert2u
  //      "PR2200ERT2U — 2200 VA / 2200 W, Line-interactive pure sine wave,
  //      6× C13 + 2× C19, 2U rack/tower, half-load 11.4 min, full-load
  //      3 min, USB + RS232 + EPO, 25.7 kg, 412 mm depth". PF=1.0 model
  //      — full VA rating available as real watts, common spec for 24+
  //      camera installs that don't justify online topology.
  {
    id: 'cyberpower-pr2200ert2u',
    manufacturer: 'CyberPower',
    model: 'PR2200ERT2U',
    category: 'ups',
    tagline:
      'PR2200ERT2U — 2200 VA / 2200 W rack/tower 2U, line-interactive sine, 6× C13 + 2× C19',
    verified: true,
    datasheetUrl: 'https://www.cyberpower.com/eu/en/product/sku/pr2200ert2u',
    specs: {
      capacityVA: 2200,
      capacityW: 2200,
      topology: 'line-interactive',
      batteryType: 'SLA',
      formFactor: 'tower-or-rack',
      outletsCount: 8,
      outletType: '6× IEC 320 C13 + 2× IEC 320 C19',
      runtimeAtHalfLoadMin: 11.4,
      runtimeAtFullLoadMin: 3,
      networkManagement: 'snmp-slot',
      weightKg: 25.7,
      depthMm: 412,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Ever — Polish manufacturer, default pick on PL CCTV installs
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://ever.eu/produkt/ups-ever-sinline-1200-usb-hid/
  //      "Sinline 1200 USB HID — 1200 VA / 780 W, line-interactive (VI),
  //      12 V / 7 Ah VRLA, 4× PN-E-93201 (Schuko) outlets, USB HID,
  //      14.5 kg, 190 × 165 × 480 mm". Runtime not published on the
  //      product page so omitted. Standard SOHO pick in PL — many small
  //      monitoring shops default to this for 4-8 camera kits.
  {
    id: 'ever-sinline-1200-usb-hid',
    manufacturer: 'Ever',
    model: 'Sinline 1200 USB HID',
    category: 'ups',
    tagline:
      'Sinline 1200 USB HID — 1200 VA / 780 W tower, line-interactive, 4× Schuko, USB HID',
    verified: true,
    datasheetUrl: 'https://ever.eu/produkt/ups-ever-sinline-1200-usb-hid/',
    specs: {
      capacityVA: 1200,
      capacityW: 780,
      topology: 'line-interactive',
      batteryType: 'SLA',
      formFactor: 'tower',
      outletsCount: 4,
      outletType: '4× Schuko (PN-E-93201)',
      networkManagement: 'usb-only',
      weightKg: 14.5,
      depthMm: 480,
    },
  },

  // SRC: https://ekocell.pl/ever-powelrine-rt-plus-2000.html
  //      "Powerline RT PLUS 2000 — 2000 VA / 2000 W (PF=1.0), VFI on-line
  //      topology, 6× 12 V / 9 Ah batteries, 8× IEC 60320 C13, rack/tower
  //      2U, runtime 18 min @ 50% / 11 min @ 80% / 6 min @ 100%,
  //      USB Type-B + RS-232 + EPO, 24 kg, 610 mm depth". SNMP via
  //      optional internal card slot. ever.eu cert expired at scan time
  //      — Polish distributor ekocell.pl quoted the Ever datasheet.
  {
    id: 'ever-powerline-rt-plus-2000',
    manufacturer: 'Ever',
    model: 'Powerline RT PLUS 2000',
    category: 'ups',
    tagline:
      'Powerline RT PLUS 2000 — 2000 VA / 2000 W rack/tower 2U, online, 8× C13, PF=1.0',
    verified: true,
    datasheetUrl: 'https://ekocell.pl/ever-powelrine-rt-plus-2000.html',
    specs: {
      capacityVA: 2000,
      capacityW: 2000,
      topology: 'online',
      batteryType: 'SLA',
      formFactor: 'tower-or-rack',
      outletsCount: 8,
      outletType: '8× IEC 320 C13',
      runtimeAtHalfLoadMin: 18,
      runtimeAtFullLoadMin: 6,
      networkManagement: 'usb+serial',
      weightKg: 24,
      depthMm: 610,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Salicru — Spanish manufacturer, EU-wide presence in commercial CCTV
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://uk-store.netceed.com/salicru-slc-1500-twin-pro2-iec-1500va-1350w-on-line-double-conversion-tower-4x-c13-18-7-min-699ro000087-h-327mm-w-190mm-d-399mm-weight-17-4kg.html
  //      "Salicru SLC-1500-TWIN PRO2 IEC — 1500VA / 1350W On-Line
  //      Double-Conversion Tower, 4× C13, 18/7 min runtime, H 327mm
  //      × W 190mm × D 399mm, 17.4 kg". USB HID + SNMP-via-intelligent-
  //      slot per directindustry. salicru.com .html pages returned 403,
  //      used netceed (UK Salicru distributor) which mirrors the
  //      datasheet. Runtime "18/7 min" likely half/full but unlabeled
  //      in available sources, so omitted to avoid guessing.
  {
    id: 'salicru-slc-1500-twin-pro2-iec',
    manufacturer: 'Salicru',
    model: 'SLC-1500-TWIN PRO2 IEC',
    category: 'ups',
    tagline:
      'SLC-1500-TWIN PRO2 IEC — 1500 VA / 1350 W tower, online double-conversion, 4× C13',
    verified: true,
    datasheetUrl:
      'https://uk-store.netceed.com/salicru-slc-1500-twin-pro2-iec-1500va-1350w-on-line-double-conversion-tower-4x-c13-18-7-min-699ro000087-h-327mm-w-190mm-d-399mm-weight-17-4kg.html',
    specs: {
      capacityVA: 1500,
      capacityW: 1350,
      topology: 'online',
      batteryType: 'SLA',
      formFactor: 'tower',
      outletsCount: 4,
      outletType: '4× IEC 320 C13',
      networkManagement: 'usb+serial',
      weightKg: 17.4,
      depthMm: 399,
    },
  },

  // SRC: https://www.laptopdirect.co.za/info-sheet.php?product=126909 (Mecer
  //      "Lobo" IVR-2400LBKS 2400 VA / 1440 W / 24 V DC-AC inverter-charger).
  //      Common South African load-shedding inverter: simulated (modified)
  //      sine wave, built-in AVR across a wide 90-280 VAC input, 20 ms transfer,
  //      10/20 A selectable charger. No internal battery — runs off an external
  //      24 V bank (2× 12 V deep-cycle, e.g. the BA gel 100Ah). Outlet count /
  //      runtime depend on the install, so omitted.
  {
    id: 'mecer-ivr-2400lbks',
    manufacturer: 'Mecer',
    model: 'IVR-2400LBKS',
    category: 'ups',
    tagline:
      'Lobo IVR-2400LBKS — 2400 VA / 1440 W 24 V line-interactive inverter-charger, simulated sine, 90–280 V AVR input, external battery bank',
    verified: true,
    datasheetUrl: 'https://www.laptopdirect.co.za/info-sheet.php?product=126909',
    specs: {
      capacityVA: 2400,
      capacityW: 1440,
      topology: 'line-interactive',
      formFactor: 'tower',
      weightKg: 4.8,
      depthMm: 300,
    },
  },
];
